/**
 * ログイン中ユーザーの表示名・ロールラベル解決 (ヘッダーユーザーメニュー用)
 *
 * 背景:
 *   Cognito の `name` 属性が未設定のユーザーでは JWT の `name` claim が空文字になり、
 *   `useAuth().user.name` が `''` となる。ヘッダーのユーザーメニューでは
 *   `{user?.name ?? 'ユーザー'}` で表示していたが、`??` は空文字を捕捉しないため
 *   名前欄が空表示になり「誰でログインしているか」が分かりづらかった。
 *   また role は単字バッジ (管/員/閲) のみで owner/admin/member/viewer のどれか
 *   直感的に分からなかった。
 *
 *   本ヘルパは「主表示にする 1 つの string」を pure 関数で返す。email を主表示に
 *   する場合も local-part のみとし、ドメインを含む raw email は主表示しない。
 *
 * 参照: docs/ubiquitous-language.md §5 (ロール正式表記)
 */
import type { SystemUserRole } from '@/types';

/**
 * ロール正式日本語ラベル (**既定ロールのみ** / docs/ubiquitous-language.md §5)。
 *
 * steering 565: カスタムロールはここに載らない。`resolveUserRoleLabel` が
 * **未知の値を素通しする**ので、組織が付けた名前がそのまま表示される。
 */
export const USER_ROLE_LABELS: Record<SystemUserRole, string> = {
  owner: '所有者',
  admin: '管理者',
  member: 'メンバー',
  viewer: '閲覧者',
  // #406 Slice 3: 入金管理専用ロール
  bank_operator: '銀行担当',
  superadmin: 'Super Admin',
};

/** ロールの説明文 (招待・ロール変更時に何ができるロールかを示す) */
export const USER_ROLE_DESCRIPTIONS: Record<SystemUserRole, string> = {
  owner: '組織のすべての機能と設定を操作できます。',
  admin: '組織のほぼすべての機能を操作できます。',
  member: '担当する案件の書類回収・資料庫・メッセージを操作できます。',
  viewer: '担当する案件の情報を閲覧できます。編集はできません。',
  bank_operator:
    '銀行明細の取込、受入口座・仮想口座の管理、入金照合を行えます。一般の案件・文書・メッセージを閲覧する権限はありません。',
  superadmin: 'プラットフォーム全体を管理できます。',
};

/** role が空 / null の場合の業務文言 */
export const USER_ROLE_LABEL_UNKNOWN = '不明';

/** `role` が既定ロール (ラベルを持つ) か判定する type guard */
function isKnownRole(role: string): role is SystemUserRole {
  return Object.prototype.hasOwnProperty.call(USER_ROLE_LABELS, role);
}

/**
 * ロールの日本語ラベルを返す pure 関数。
 *
 * - 既知の `UserRole` → 正式日本語ラベル
 * - 未知の非空 role → raw 値を passthrough (BE に role 追加された際も値が消えない)
 * - 空文字 / null / undefined → `不明`
 */
export function resolveUserRoleLabel(role: string | null | undefined): string {
  const trimmed = typeof role === 'string' ? role.trim() : '';
  if (isKnownRole(trimmed)) {
    return USER_ROLE_LABELS[trimmed];
  }
  return trimmed.length > 0 ? trimmed : USER_ROLE_LABEL_UNKNOWN;
}

/**
 * ログインユーザーの主表示名を返す pure 関数。
 *
 * 優先順位:
 *   1. `name` が trim 後に非空 → その trim 済み文字列
 *   2. `name` が空 → `email` の local-part (`@` より前)。email に `@` が無ければ
 *      email 文字列そのもの (trim 済み)
 *   3. いずれも空 → `ユーザー`
 *
 * raw email (ドメイン込み) を主表示として返すことはない。
 */
export function resolveUserDisplayName(
  name: string | null | undefined,
  email: string | null | undefined,
): string {
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (trimmedName.length > 0) {
    return trimmedName;
  }

  const trimmedEmail = typeof email === 'string' ? email.trim() : '';
  if (trimmedEmail.length > 0) {
    const localPart = trimmedEmail.split('@')[0].trim();
    if (localPart.length > 0) {
      return localPart;
    }
  }

  return 'ユーザー';
}

/**
 * ヘッダーのユーザーメニュー用の主表示名を返す pure 関数。
 *
 * AceCollect users profile の `displayName` (DynamoDB 正本、ユーザー管理一覧と同じ値) を
 * 最優先で表示し、取得できない場合のみ JWT 由来の値にフォールバックする。
 *
 * 優先順位:
 *   1. `displayName` (profile / `/admin/users/me` 由来) が trim 後に非空 → その trim 済み文字列
 *   2. それ以外 → `resolveUserDisplayName(name, email)` に委譲
 *      (JWT `name` → email local-part → `ユーザー` の既存 #275 挙動を維持)
 *
 * 背景: ログイン中ユーザーの `AuthUser.name` は Cognito ID トークンの `name` claim 由来で、
 *   属性未設定ユーザーでは空になり email local-part が表示されていた。氏名 (displayName) は
 *   DynamoDB users テーブルにあるため、self-scoped API でそれを取得して優先表示する。
 */
export function resolveHeaderDisplayName(
  displayName: string | null | undefined,
  name: string | null | undefined,
  email: string | null | undefined,
): string {
  const trimmedDisplay = typeof displayName === 'string' ? displayName.trim() : '';
  if (trimmedDisplay.length > 0) {
    return trimmedDisplay;
  }
  return resolveUserDisplayName(name, email);
}

/**
 * アバターのイニシャル円に使う **氏名** を返す pure 関数 (氏名が無ければ `undefined`)。
 *
 * 優先順位:
 *   1. `displayName` (profile / `/admin/users/me` 由来) が trim 後に非空 → その trim 済み文字列
 *   2. `name` (JWT `name` claim) が trim 後に非空 → その trim 済み文字列
 *   3. どちらも空 → `undefined` (呼び出し側は人物アイコンへ落とす)
 *
 * **email / email local-part は決して返さない。** `resolveHeaderDisplayName` は表示名として
 * email local-part までフォールバックするが、その結果をイニシャルに使うと氏名未設定ユーザーで
 * `k` のようなメール先頭 1 文字が出てしまう。表示名の fallback とイニシャルの seed は
 * 目的が違うので、ここで別の関数として分けている
 * (`UserAvatar` の `initialsSeed` も「email をそのまま渡してはならない」と定めている)。
 */
export function resolveAvatarInitialSeed(
  displayName: string | null | undefined,
  name: string | null | undefined,
): string | undefined {
  const trimmedDisplay = typeof displayName === 'string' ? displayName.trim() : '';
  if (trimmedDisplay.length > 0) {
    return trimmedDisplay;
  }

  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (trimmedName.length > 0) {
    return trimmedName;
  }

  return undefined;
}
